import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import { Avatar, Checkbox } from '@nackle/paper';
import { LocaleString, getString } from '@nackle/intl-tools';
import { profileUrl } from '../../../endpoints';
import { UserOutlined } from '@ant-design/icons';
import BudgetMeter from '../../shared/budget-meter/BudgetMeter.jsx';
import PointEntry from './PointEntry';
import { setAllocatePoints, setPointsForPerson } from './actions';
import { getSelectedProgram } from '../program/selectors';
import { getAwardValue, getAllocatePoints, getRemainingBudget, getRemainingBudgetPercent } from './selectors';
import { getSelectedPeople } from '../people/selectors';

export default function Awards( props ) {
    const { company, 
        forward, 
        integrationType } = props;

    const dispatch = useDispatch();
    const selectedPeople = useSelector( ( state ) => getSelectedPeople( state ) );
    const selectedProgram = useSelector( ( state ) => getSelectedProgram( state ) );
    const awardValue = useSelector( ( state ) => getAwardValue( state ) );
    const allocatePoints = useSelector( ( state ) => getAllocatePoints( state ) );
    const remainingBudget = useSelector( ( state ) => getRemainingBudget( state ) );
    const remainingBudgetPercent = useSelector( ( state ) => getRemainingBudgetPercent( state ) );
    
    const [ animate, setAnimate ] = useState( false );
    const { minAward, maxAward } = selectedProgram;

    useEffect( () => {
        setAnimate( true );
    }, [] );
    
    const togglePointAllocation = ( event ) => {
        dispatch( setAllocatePoints( event.target.checked ) );
    };

    const handleNumberInputChange = ( value, recipientId ) => {
        dispatch( setPointsForPerson( recipientId, value ) );
    };
    
    return (
        <CSSTransition
        timeout={ 1000 }
        in={ animate }
        classNames={ forward ? 'slide-in' : 'slide-out' }>
            <div className='awards-container'>
                <span className='input-label'><LocaleString code='recognition.program.pointsHeader'/></span>
                <span className='input-label-extra'>{ !selectedProgram.pointsRequired 
                    ? getString( 'recognition.header.optional' ) 
                    : getString( 'recognition.header.required' ) }</span>
                {
                    selectedProgram && selectedProgram.hasPoints &&
                    <div className='budget-slider'>
                            <BudgetMeter data-testid='budgetMeter'
                                percent={ remainingBudgetPercent }
                                current={ remainingBudget }
                                />
                    </div>
                }
                { remainingBudget < 0 &&
                    <div className='error-text'>
                        <LocaleString code='recognition.error.tooManyPoints' /></div>
                }
                { selectedPeople.length > 1 && !selectedProgram.isFixed &&
                <div className='allocate-points'>
                    <Checkbox data-testid='allocatePointsCheckbox' onChange={ togglePointAllocation }
                                checked={ allocatePoints }>
                        <LocaleString code='recognition.program.differentPoints' />
                    </Checkbox>
                </div>
                }
                { !allocatePoints ?
                    <div>  
                        <PointEntry 
                            pointMin={ selectedProgram.pointsRequired || selectedProgram.isFixed ? minAward : 0 } 
                            pointMax={ maxAward } 
                            onChange={ ( value ) => handleNumberInputChange( value, 'all' ) }
                            onFocus={ ( e ) => e.target.value == 0 ? handleNumberInputChange( selectedProgram.isFixed ? maxAward : '', 'all' ) : null }
                            value={ awardValue }
                            disabled={ ( selectedProgram.isFixed && selectedProgram.pointsRequired ) || remainingBudget === 0 }
                        />
                        { !selectedProgram.isFixed &&
                            <div className='point-range'>
                                <LocaleString code='recognition.program.rangeInstructions' replaceOptions={ { min: minAward, max: maxAward } } />
                            </div>
                        }
                    </div>
                :
                    <div className='allocate-points-container'>
                        <div className='point-range'>
                            <LocaleString code='recognition.program.rangePerPersonInstructions' replaceOptions={ { min: minAward, max: maxAward } } />
                        </div>
                        { selectedPeople.map( ( recipient ) => {
                            return <div key={ `recipient-award-${ recipient.id }` } className='recipient-point-row'>
                                <div>
                                    <Avatar className="primary-background-avatar" src={ `${ profileUrl }/public/images/image/${ company.id }/${ recipient.id }/avatar` } icon={ integrationType === 'WORKPLACE' ? <UserOutlined /> : null }>{ `${ recipient.givenName.slice( 0, 1 ) }${ recipient.surname.slice( 0, 1 ) }` }</Avatar>
                                </div>
                                <div className='recipient-info'>
                                    <div className='team-recipient-name'>
                                        { `${ recipient.givenName } ${ recipient.surname }` }
                                    </div>
                                    <div className='recipient-additionalInfo'>
                                        { `${ recipient.jobTitle }, ${ recipient.dept }, ${ recipient.manager }, ${ recipient.country }` }
                                    </div>
                                </div>
                                <div className='point-entry'>
                                    <PointEntry 
                                        pointMin={ selectedProgram.pointsRequired ? minAward : 0 } 
                                        pointMax={ maxAward } 
                                        onChange={ ( value ) => handleNumberInputChange( value, recipient.id ) }
                                        onFocus={ ( e ) =>  e.target.value == 0 ? handleNumberInputChange( '', recipient.id ) : null }
                                        value={ recipient.awardValue } 
                                        disabled={ selectedProgram.isFixed && selectedProgram.pointsRequired }
                                    />
                                </div>
                            </div>;
                        } ) }
                    </div>
                }   
            </div>
        </CSSTransition>
    );
}
